export interface ComparisonProduct {
  name: string
  brand: string
  rating: number
  price: string
  image: string
  overall: string
  reviewSlug?: string
  affiliateUrl: string
  pros: string[]
  cons: string[]
}

export interface ComparisonFeature {
  feature: string
  left: string | boolean
  right: string | boolean
}

export interface ComparisonUseCase {
  scenario: string
  winner: string
  reason: string
}

export interface ComparisonGuide {
  slug: string
  title: string
  subtitle: string
  leftLabel: string
  rightLabel: string
  products: [ComparisonProduct, ComparisonProduct]
  features: ComparisonFeature[]
  useCases: ComparisonUseCase[]
  verdict: string[]
  relatedReviewSlugs: string[]
  seoTitle: string
  seoDescription: string
}

export const comparisonGuides: ComparisonGuide[] = [
  {
    slug: "arko-vital-vs-b-vital",
    title: "Arko Vital vs. B Vital: Which is Better for Energy?",
    subtitle: "Comprehensive comparison of two leading B-complex supplements",
    leftLabel: "B Vital Total",
    rightLabel: "Arko Vital",
    products: [
      {
        name: "B Vital Total",
        brand: "Vitamin Vital",
        rating: 4.8,
        price: "$24.99",
        image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=300&h=300&fit=crop",
        overall: "Best for sustained energy and mental clarity",
        reviewSlug: "b-vital-total",
        affiliateUrl: "#",
        pros: [
          "Complete 8-vitamin B complex",
          "High bioavailability forms (methylated B12 & folate)",
          "Third-party tested for purity",
          "Vegan-friendly capsules",
          "No artificial additives",
          "Gentle on stomach"
        ],
        cons: [
          "Slightly higher price point",
          "Requires 2 capsules daily"
        ]
      },
      {
        name: "Arko Vital B Complex",
        brand: "Arko",
        rating: 4.3,
        price: "$18.99",
        image: "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=300&h=300&fit=crop",
        overall: "Budget-friendly option with standard formulation",
        affiliateUrl: "#",
        pros: [
          "Lower price point",
          "Widely available in stores",
          "Single-capsule daily dose",
          "Established European brand"
        ],
        cons: [
          "Uses synthetic B12 (cyanocobalamin)",
          "Lower B vitamin dosages",
          "Contains artificial colors",
          "No third-party testing certificate",
          "Some users report nausea"
        ]
      }
    ],
    features: [
      { feature: "B1 (Thiamine)", left: "100mg", right: "2.8mg" },
      { feature: "B2 (Riboflavin)", left: "50mg", right: "3.2mg" },
      { feature: "B3 (Niacin)", left: "75mg", right: "32mg" },
      { feature: "B5 (Pantothenic Acid)", left: "100mg", right: "12mg" },
      { feature: "B6 (Pyridoxine)", left: "50mg (P-5-P form)", right: "4mg" },
      { feature: "B7 (Biotin)", left: "300mcg", right: "150mcg" },
      { feature: "B9 (Folate)", left: "400mcg (Methylfolate)", right: "200mcg (Folic Acid)" },
      { feature: "B12 (Cobalamin)", left: "1000mcg (Methylcobalamin)", right: "6mcg (Cyanocobalamin)" },
      { feature: "Third-Party Tested", left: true, right: false },
      { feature: "Vegan-Friendly", left: true, right: true },
      { feature: "No Artificial Colors", left: true, right: false },
      { feature: "Bioavailable Forms", left: true, right: false }
    ],
    useCases: [
      {
        scenario: "Chronic Fatigue & Low Energy",
        winner: "B Vital Total",
        reason: "Higher dosages of energy-critical B vitamins (B1, B2, B5, B12) provide more therapeutic benefits for fatigue."
      },
      {
        scenario: "General Wellness Maintenance",
        winner: "Either",
        reason: "Both products work for basic B vitamin supplementation, though B Vital offers better value long-term."
      },
      {
        scenario: "Budget-Conscious Buyers",
        winner: "Arko Vital",
        reason: "Lower upfront cost makes it more accessible, though cost-per-dose effectiveness favors B Vital."
      },
      {
        scenario: "Athletes & High Performers",
        winner: "B Vital Total",
        reason: "Methylated forms and higher potencies support intense physical and mental demands better."
      },
      {
        scenario: "Sensitive Stomachs",
        winner: "B Vital Total",
        reason: "Cleaner formulation without artificial additives reduces digestive discomfort."
      }
    ],
    verdict: [
      "B Vital Total emerges as the superior choice for most users seeking comprehensive energy support. Its use of methylated, bioavailable forms of B12 and folate ensures better absorption, particularly for individuals with MTHFR gene variations. The higher potencies across all B vitamins provide therapeutic benefits beyond basic nutritional support.",
      "Arko Vital serves as an acceptable budget option for basic maintenance, but falls short in several critical areas: lower dosages, synthetic forms, and the inclusion of artificial additives may limit its effectiveness and tolerability.",
      "For sustained energy, mental clarity, and long-term value, B Vital Total is our clear recommendation. The slightly higher price translates to significantly better quality and efficacy."
    ],
    relatedReviewSlugs: ["b-vital-total", "d-vital-10000", "immunovit-plus"],
    seoTitle: "Arko Vital vs B Vital: Which Is Better for Energy? | Vitamin Vital Portal",
    seoDescription: "Compare Arko Vital vs B Vital for energy, dosage, ingredients, and value. See the Vitamin Vital Portal verdict and check the best price on B Vital Total."
  }
]

export function getComparisonBySlug(slug: string | undefined): ComparisonGuide | undefined {
  if (!slug) return undefined
  return comparisonGuides.find((guide) => guide.slug === slug)
}
